import React from 'react';
import {
  Archive,
  Copy,
  Download,
  Edit3,
  Folder,
  Loader2,
  MoreHorizontal,
  Pause,
  Play,
  Send,
  Settings,
  Trash2,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { getWorkflowStatusValue } from '../../utils/workflowLibraryUtils';

function runMenuAction(event, callback) {
  event.preventDefault();
  event.stopPropagation();
  callback?.();
}

const WorkflowActionsMenu = ({
  workflow,
  variant = 'platform',
  onOpenBuilder,
  onSendTest,
  onPause,
  onActivate,
  onDuplicate,
  onDownload,
  downloadingWorkflowId = null,
  onMoveToFolder,
  onArchive,
  onDelete,
  openBuilderLabel,
  triggerClassName,
  menuClassName,
  'data-testid': dataTestId,
}) => {
  const isActive = getWorkflowStatusValue(workflow) === 'active';
  const isAgency = variant === 'agency';
  const workflowId = workflow?.id || workflow?.workflow_id;
  const isDownloading = Boolean(
    downloadingWorkflowId && workflowId && downloadingWorkflowId === workflowId,
  );
  const itemClass = isAgency
    ? 'text-slate-700 focus:bg-slate-100 focus:text-slate-900'
    : 'text-zinc-300 focus:bg-zinc-800 focus:text-white';
  const separatorClass = isAgency ? 'bg-slate-200' : 'bg-zinc-700';
  const menuSurfaceClass = isAgency ? 'bg-white border-slate-200' : 'bg-zinc-900 border-zinc-700';
  const builderLabel = openBuilderLabel || (isAgency ? 'Edit Workflow' : 'Open Builder');
  const BuilderIcon = isAgency ? Edit3 : Settings;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('h-8 w-8 p-0', isAgency ? 'text-slate-500' : 'text-zinc-400', triggerClassName)}
          data-testid={dataTestId || `workflow-actions-trigger-${workflow?.id}`}
          onClick={(event) => event.stopPropagation()}
        >
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn('w-48', menuSurfaceClass, menuClassName)}
        data-testid={`workflow-actions-menu-${workflow?.id}`}
        onClick={(event) => event.stopPropagation()}
      >
        <DropdownMenuItem
          data-testid={`workflow-action-open-builder-${workflow?.id}`}
          className={itemClass}
          onSelect={(event) => runMenuAction(event, onOpenBuilder)}
        >
          <BuilderIcon className="w-4 h-4 mr-2" />
          {builderLabel}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`workflow-action-send-test-${workflow?.id}`}
          className={itemClass}
          onSelect={(event) => runMenuAction(event, onSendTest)}
        >
          <Send className="w-4 h-4 mr-2" />
          Send Test
        </DropdownMenuItem>
        <DropdownMenuSeparator className={separatorClass} />
        {isActive ? (
          <DropdownMenuItem
            data-testid={`workflow-action-pause-${workflow?.id}`}
            className={isAgency ? 'text-amber-700 focus:bg-amber-50' : 'text-amber-400 focus:bg-zinc-800 focus:text-amber-300'}
            onSelect={(event) => runMenuAction(event, onPause)}
          >
            <Pause className="w-4 h-4 mr-2" />
            Pause
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            data-testid={`workflow-action-activate-${workflow?.id}`}
            className={isAgency ? 'text-emerald-700 focus:bg-emerald-50' : 'text-emerald-400 focus:bg-zinc-800 focus:text-emerald-300'}
            onSelect={(event) => runMenuAction(event, onActivate)}
          >
            <Play className="w-4 h-4 mr-2" />
            Activate
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          data-testid={`workflow-action-duplicate-${workflow?.id}`}
          className={itemClass}
          onSelect={(event) => runMenuAction(event, onDuplicate)}
        >
          <Copy className="w-4 h-4 mr-2" />
          Duplicate
        </DropdownMenuItem>
        {typeof onDownload === 'function' ? (
          <DropdownMenuItem
            data-testid={`workflow-action-download-${workflowId}`}
            className={itemClass}
            disabled={isDownloading}
            onSelect={(event) => {
              if (isDownloading) {
                event.preventDefault();
                event.stopPropagation();
                return;
              }
              runMenuAction(event, () => onDownload(workflow));
            }}
          >
            {isDownloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          data-testid={`workflow-action-move-folder-${workflow?.id}`}
          className={itemClass}
          onSelect={(event) => runMenuAction(event, onMoveToFolder)}
        >
          <Folder className="w-4 h-4 mr-2" />
          Move to Folder
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`workflow-action-archive-${workflow?.id}`}
          className={itemClass}
          onSelect={(event) => runMenuAction(event, onArchive)}
        >
          <Archive className="w-4 h-4 mr-2" />
          Archive
        </DropdownMenuItem>
        <DropdownMenuSeparator className={separatorClass} />
        <DropdownMenuItem
          data-testid={`workflow-action-delete-${workflow?.id}`}
          className="text-red-500 focus:bg-red-50 focus:text-red-600"
          onSelect={(event) => runMenuAction(event, onDelete)}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default WorkflowActionsMenu;
